import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BackButton from './BackButton';
import BurgerMenu from './BurgerMenu';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const faqCategories: FAQCategory[] = [
    {
      title: "Getting Started",
      items: [
    {
      question: "What is TourBud?",
          answer: "TourBud is an AI-powered tour platform that creates personalized audio tours based on your exact location and interests. Using advanced AI research, we generate comprehensive tours with detailed narration, historical facts (all cited with sources), and professional audio you can listen to while exploring. Every tour you create becomes available to the rest of the community, building a shared library of local knowledge."
        },
        {
          question: "How do I create my first tour?",
          answer: (
            <div>
              <p style={{ marginBottom: '12px' }}>First, sign in with Google from the home page. Then:</p>
              <ol style={{ paddingLeft: '20px', margin: '0', lineHeight: '1.8' }}>
                <li>Tap 'Create A Tour'</li>
                <li>Confirm or enter your location (searches work too!)</li>
                <li>Select 1-2 interests from 20 categories</li>
                <li>Choose up to 3 specific places you want to learn about (or skip this step)</li>
                <li>Wait 1-3 minutes while our AI researches and creates your tour</li>
              </ol>
            </div>
          )
        },
        {
          question: "What interests can I choose from?",
          answer: (
            <div>
              <p style={{ marginBottom: '16px' }}>We offer 20 interest categories. You can select up to 2 interests per tour to keep the content focused and relevant:</p>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px'
              }}>
                {[
                  { icon: '🏛️', name: 'History' },
                  { icon: '🏗️', name: 'Architecture' },
                  { icon: '🎨', name: 'Art' },
                  { icon: '🎭', name: 'Culture' },
                  { icon: '🗳️', name: 'Politics' },
                  { icon: '⛪', name: 'Religion' },
                  { icon: '🔍', name: 'Crime' },
                  { icon: '⚔️', name: 'War' },
                  { icon: '💰', name: 'Economy' },
                  { icon: '💼', name: 'Business' },
                  { icon: '💡', name: 'Innovation' },
                  { icon: '🌿', name: 'Nature' },
                  { icon: '📚', name: 'Literature' },
                  { icon: '🎵', name: 'Music' },
                  { icon: '🎬', name: 'Film & TV' },
                  { icon: '✨', name: 'Design' },
                  { icon: '🍽️', name: 'Food' },
                  { icon: '🐉', name: 'Legends' },
                  { icon: '🔬', name: 'Science' },
                  { icon: '👗', name: 'Fashion' }
                ].map((interest) => (
                  <div
                    key={interest.name}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: 'var(--secondary-color)',
                      borderRadius: '20px',
                      fontSize: '13px',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>{interest.icon}</span>
                    <span>{interest.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )
        },
        {
          question: "How long does tour generation take?",
          answer: "Tour generation typically takes 1 to 3 minutes, depending on the amount of interests selected. During this time, our AI searches online sources, gathers facts about your location and selected places, researches relevant historical information, and creates personalized narration. The audio is then generated automatically."
        },
        {
          question: "Do I need to be logged in?",
          answer: "You can browse nearby tours without logging in (we show you 3 nearby tours if not logged in), but you need to sign in with Google to create tours, save tours to your collection, or purchase credits. Sign in is quick and secure through Google authentication."
        }
      ]
    },
    {
      title: "Credits & Payments",
      items: [
        {
          question: "How does the credit system work?",
          answer: "Each tour generation costs 1 credit. Your credit balance is checked before starting generation. Once a tour is created, the credit is deducted and the tour is automatically saved to your account. You can purchase credits individually or through a monthly subscription."
        },
        {
          question: "What are the pricing options?",
          answer: (
            <div>
              <p style={{ marginBottom: '12px', fontWeight: '600' }}>One-time purchases:</p>
              <ul style={{ paddingLeft: '20px', margin: '0 0 16px 0', lineHeight: '1.8' }}>
                <li>1 tour for $12</li>
                <li>3 tours for $30</li>
                <li>5 tours for $45</li>
              </ul>
              <p style={{ marginBottom: '12px', fontWeight: '600' }}>Monthly subscriptions:</p>
              <ul style={{ paddingLeft: '20px', margin: '0 0 16px 0', lineHeight: '1.8' }}>
                <li>2 tours/month for $18</li>
                <li>5 tours/month for $40</li>
                <li>10 tours/month for $85</li>
              </ul>
              <p style={{ marginBottom: '16px' }}>All payments are securely processed through Paystack.</p>
              <button
                onClick={() => navigate('/buy')}
                className="btn btn-primary"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px'
                }}
              >
                View Pricing & Buy Credits
              </button>
            </div>
          )
        },
        {
          question: "What's the difference between one-time and subscription credits?",
          answer: "One-time credits never expire and can be used anytime. Subscription credits refresh monthly - you get your allotment at the start of each billing period. Subscriptions can be cancelled anytime, and you'll keep access until the end of your billing period."
        },
        {
          question: "Can I see my purchase history?",
          answer: "Yes! Go to Account from the menu and you'll see your complete transaction history, including the date, amount, credits added, and payment status for all your purchases."
        }
      ]
    },
    {
      title: "Tours & Content",
      items: [
        {
          question: "What's included in each tour?",
          answer: "Each tour includes: a custom title and description, detailed narration with historical facts and stories, research citations with links to sources, professional audio narration you can listen to, estimated duration and distance, and information about specific places you selected."
        },
        {
          question: "How does the place selection work?",
          answer: "After selecting your interests, we find nearby places using Google Maps and organize them by category (Entertainment, Food & Drink, Shopping, Education, etc.). This is optional if you want to learn about specific nearby places. You can select up to 3 specific places, or skip this step entirely. If no places are selected, we focus on the street and area instead."
        },
        {
          question: "Can I browse tours created by others?",
          answer: "Yes! The home page shows nearby tours created by other users, sorted by distance from you. Each tour shows its location, distance from you, view count, and how many people have saved it. You can browse without logging in, but need to sign in to save tours."
        },
        {
          question: "Where are my tours saved?",
          answer: "All tours you create are automatically saved. Access them from 'Collection' in the menu, which shows both tours you've created and tours you've saved from other users, sorted by most recent first."
        },
        {
          question: "Can I delete a tour I created?",
          answer: "Yes, you can delete tours you've created from your Collection (Past Tours). Swipe left on any tour to reveal the delete option. Note: This permanently removes the tour."
        },
        {
          question: "What happens if I leave during tour generation?",
          answer: "Don't worry! If you close the app or navigate away during generation, the tour continues being created in the background. When you return, you'll see a notification letting you resume and view your completed tour."
        }
      ]
    },
    {
      title: "Location & Settings",
      items: [
        {
          question: "How does location work?",
          answer: "You can either use your current location (requires browser permission) or search for any address or place. Your location is cached for 30 minutes to save battery. We use Google Places to find interesting spots near your location. If location permission is denied, we'll try IP-based geolocation as a backup."
        },
        {
          question: "Can I create tours for locations I'm not at?",
          answer: "Absolutely! On the location screen, just tap the search box and type any address, landmark, or place name. This is perfect for planning tours before you travel or exploring areas you can't physically visit."
        },
        {
          question: "Can I change distance units?",
          answer: "Yes! Open the menu (top left) and tap the distance toggle to switch between miles and kilometers. Your preference is saved and applies to all distances shown throughout the app."
        },
        {
          question: "Is my location data private?",
          answer: "Yes. Your location is only used to find nearby places and generate tours. It's stored locally on your device (cached for 30 minutes) and sent only to our secure servers during tour generation. We never share location data with third parties."
        }
      ]
    },
    {
      title: "Audio & Sharing",
      items: [
        {
          question: "How does the audio work?",
          answer: "Audio is automatically generated for every tour using professional text-to-speech technology. You can play, pause, skip forward/backward 15 seconds, and scrub through the timeline. The audio player shows current time, total duration, and has hover previews for precise navigation."
        },
        {
          question: "What if the audio isn't ready?",
          answer: "Audio takes 2 to 3 minutes to generate and happens automatically after tour creation. If it fails, you'll see a retry button. The tour text is always available immediately, even while audio is processing."
        },
        {
          question: "Can I share tours with friends?",
          answer: "Yes! Each tour has a share button that lets you send the tour link via your device's share menu (text, email, social media, etc.). Friends can view the tour without logging in, and they can save it to their own collection if they sign in."
        },
        {
          question: "Can I use TourBud offline?",
          answer: "You need internet to generate tours and load tour content. Once a tour is loaded, the text content is available, but audio streaming and maps require a connection. We recommend having data or WiFi while using tours."
        }
      ]
    },
    {
      title: "Legal & Policies",
      items: [
        {
          question: "What are your Terms of Service?",
          answer: (
            <div>
              <p style={{ marginBottom: '16px' }}>
                Our Terms of Service outline the rules and guidelines for using TourBud, including user responsibilities, 
                intellectual property rights, and service limitations. Please review them to understand your rights and obligations.
              </p>
              <button
                onClick={() => navigate('/terms')}
                className="btn btn-primary"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px'
                }}
              >
                View Terms of Service
              </button>
            </div>
          )
        },
        {
          question: "How do you handle my privacy?",
          answer: (
            <div>
              <p style={{ marginBottom: '16px' }}>
                We take your privacy seriously. Our Privacy Policy explains what data we collect, how we use it, 
                and how we protect your information. We never sell your personal data to third parties.
              </p>
              <button
                onClick={() => navigate('/privacy')}
                className="btn btn-primary"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px'
                }}
              >
                View Privacy Policy
              </button>
            </div>
          )
        },
        {
          question: "What is your refund policy?",
          answer: (
            <div>
              <p style={{ marginBottom: '16px' }}>
                We can only offer refunds for failed tour generations where the system was unable to create your tour. 
                Once a tour has been successfully generated, no refunds are available as the AI research and content 
                creation have been completed. If you experience a failed generation, please contact support for assistance.
              </p>
              <button
                onClick={() => navigate('/refund-policy')}
                className="btn btn-primary"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px'
                }}
              >
                View Full Refund Policy
              </button>
            </div>
          )
        }
      ]
    }
  ];

  const toggleQuestion = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };

  return (
    <div className="app">
      <div className="header">
        <BackButton onClick={() => navigate('/home')} />
        <h2 className="header-title">How It Works</h2>
        <BurgerMenu />
      </div>

      <div className="container">

        {/* FAQ Categories */}
        <div style={{
          paddingBottom: '20px'
        }}>
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} style={{ marginBottom: categoryIndex === faqCategories.length - 1 ? '0px' : '32px' }}>
              {/* Category Title */}
              <motion.h3
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: categoryIndex * 0.1 }}
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'var(--primary-color)',
                  marginBottom: '16px',
                  paddingLeft: '4px'
                }}
              >
                {category.title}
              </motion.h3>

              {/* Category Items */}
              {category.items.map((item, itemIndex) => {
                const uniqueId = `${categoryIndex}-${itemIndex}`;
                return (
            <motion.div
                    key={uniqueId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: (categoryIndex * 0.1) + (itemIndex * 0.05) }}
              style={{
                marginBottom: '12px',
                backgroundColor: 'var(--card-background)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden'
              }}
            >
            <button
                      onClick={() => toggleQuestion(uniqueId)}
              style={{
                width: '100%',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--secondary-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                flex: 1,
                paddingRight: '16px'
              }}>
                {item.question}
              </span>
                      {openIndex === uniqueId ? (
                <ChevronUp size={20} color="var(--primary-color)" />
              ) : (
                <ChevronDown size={20} color="var(--text-secondary)" />
              )}
            </button>
            
            <AnimatePresence>
                      {openIndex === uniqueId && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    padding: '0 20px 16px 20px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: 'var(--text-secondary)',
                    borderTop: '1px solid var(--border-color)',
                    paddingTop: '16px'
                  }}>
                    {typeof item.answer === 'string' ? item.answer : item.answer}
                  </div>
                  </motion.div>
              )}
            </AnimatePresence>
            </motion.div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Contact Support Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{
            marginTop: '8px',
            paddingBottom: '20px'
          }}
        >
          <div style={{
            backgroundColor: 'var(--card-background)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              margin: '0 0 8px 0'
            }}>
              Still Need Help?
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              margin: '0 0 16px 0'
            }}>
              Can't find the answer you're looking for? Get in touch with us.
            </p>
            <a
              href="mailto:miaitorapp@gmail.com"
              className="btn btn-primary"
              style={{
                display: 'inline-block',
                textDecoration: 'none'
              }}
            >
              Contact Support
            </a>
          </div>
        </motion.div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '24px 0 20px 0',
          fontSize: '12px',
          color: 'var(--text-secondary)',
          opacity: 0.7,
          lineHeight: '1.6'
        }}>
          <div>© 2025 Miaitor - All Rights Reserved</div>
          <a 
            href="https://SalvBarras.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              color: 'var(--text-secondary)',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.7';
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            Designed & Developed With ❤️ By: Salvatore
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;

